'use client';

import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { adventures } from '@/lib/data';
import { useActivityVisibility } from '@/hooks/useActivityVisibility';
import { useDynamicActivities } from '@/hooks/useDynamicActivities';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import styles from './collage3.module.css';
import { BreadcrumbSchema } from '@/lib/schema';


/**
 * COLLAGE OPTION 3: DIAGONAL OVERLAP
 *
 * Diseno con capas diagonales entrelazadas
 * - Items con rotacion sutil
 * - Overlap estrategico para profundidad
 * - Animacion parallax en scroll
 *
 * Now also renders dynamic activities from Firestore (created via admin),
 * merged with the static adventures list.
 */
export default function AdventuresPage() {
  const t = useTranslations('guidedAdventures');
  const tAdv = useTranslations('adventures');
  const tCommon = useTranslations('common');
  const params = useParams();
  const rawLocale = params.locale as string;
  const locale = (['en', 'es', 'pt'].includes(rawLocale) ? rawLocale : 'en') as 'en' | 'es' | 'pt';
  const { isVisible } = useActivityVisibility();
  const { activities: dynamicActivities } = useDynamicActivities();

  // Static adventure IDs for dedup
  const staticIds = new Set(adventures.map(a => a.id));

  // Dynamic-only activities (not in static list)
  const dynamicOnly = dynamicActivities.filter(
    a => !staticIds.has(a.slug) && !staticIds.has(a.id)
  );

  // Visible static adventures
  const visibleStatic = adventures.filter(a => isVisible(a.id, a.hidden));

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
      ]} />
      <link rel="preload" as="image" href="/background/adventures-collage-background.webp" />
      <Header />
      <main className={styles.collageWrapper}>
        <section className={styles.collageIntro}>
          <span className={styles.label}>{t('index.label')}</span>
          <h1 className={styles.title}>{t('index.title')}</h1>
          <p className={styles.subtitle}>{t('index.subtitle')}</p>
        </section>

        <section className={styles.diagonalGrid}>
          {/* Static adventures (have dedicated pages under /guided-adventures/) */}
          {visibleStatic.map((adventure, index) => (
            <a
              key={adventure.id}
              href={`/${locale}/guided-adventures/${adventure.id}/`}
              className={styles.diagonalItem}
              data-index={index}
              style={{
                '--index': index,
                '--rotation': index % 2 === 0 ? '2deg' : '-2deg',
              } as React.CSSProperties}
            >
              <div className={styles.itemCard}>
                <div
                  className={styles.itemBg}
                  style={{ backgroundImage: `url(${adventure.heroImage})` }}
                />
                <div className={styles.itemOverlay}>
                  <h3 className={styles.itemTitle}>{tAdv(`${adventure.id}.title`)}</h3>
                  <p className={styles.itemDescription}>{tAdv(`${adventure.id}.description`).substring(0, 80)}...</p>
                </div>
              </div>
            </a>
          ))}

          {/* Dynamic activities from Firestore (link to marketing page) */}
          {dynamicOnly.map((activity, i) => {
            const index = visibleStatic.length + i;
            const description = activity.description || '';
            return (
              <a
                key={activity.id}
                href={`/${locale}/guided-adventures/${activity.slug}/`}
                className={styles.diagonalItem}
                data-index={index}
                style={{
                  '--index': index,
                  '--rotation': index % 2 === 0 ? '2deg' : '-2deg',
                } as React.CSSProperties}
              >
                <div className={styles.itemCard}>
                  <div
                    className={`${styles.itemBg} ${!activity.heroImage ? styles.itemBgPlaceholder : ''}`}
                    style={activity.heroImage ? { backgroundImage: `url(${activity.heroImage})` } : undefined}
                  />
                  <div className={styles.itemOverlay}>
                    <h3 className={styles.itemTitle}>{activity.name}</h3>
                    <p className={styles.itemDescription}>
                      {description ? description.substring(0, 80) + '...' : ''}
                    </p>
                  </div>
                </div>
              </a>
            );
          })}
        </section>

      </main>
      <Footer />
    </>
  );
}
